import {
  createContext,
  Dispatch,
  useContext,
  FunctionComponent,
  useReducer,
  useState,
} from 'react';
import { GetTenderAttachmentResponse } from '~src/services/api/tender';
import { DbFile, DbModuleFile } from '~src/types/db';

export type FileType =
  | DbFile
  | DbModuleFile
  | GetTenderAttachmentResponse
  | (Record<string, any> & unknown)
  | undefined;
export type FileLoadingState = {
  isLoadingInProgress: boolean;
};

export enum FileViewerMargins {
  NO_MARGIN = '0',
  TABS = '45px',
}

type SetFileToViewFunc = (
  file: FileType | undefined,
  props?: { marginTop?: FileViewerMargins }
) => void;

export type FileToViewCtx = {
  fileToView: FileType | undefined;
  setFileToView: SetFileToViewFunc;
  loadingState: FileLoadingState;
  setLoadingState: Dispatch<FileLoadingState>;
  viewerTopMargin: FileViewerMargins;
  setViewerTopMargin: Dispatch<FileViewerMargins>;
  primaryPreviewFile: FileType;
  setPrimaryPreviewFile: Dispatch<FileType>;
};

const FileToViewContext = createContext({} as FileToViewCtx);

export const FileToViewProvider: FunctionComponent = ({ children }) => {
  const [loadingState, setLoadingState] = useState({
    isLoadingInProgress: true,
  });

  const [viewerTopMargin, setViewerTopMargin] = useState<FileViewerMargins>(
    FileViewerMargins.NO_MARGIN
  );

  const [fileToView, setFileToView] = useReducer(
    (
      _: DbFile | DbModuleFile | undefined,
      newValue: DbFile | DbModuleFile | undefined
    ) => newValue,
    undefined
  );

  // new viewer target, used by "primary svf preview" shortcut
  const [primaryPreviewFile, setPrimaryPreviewFile] = useReducer(
    (_: FileType, newValue: FileType) => newValue,
    undefined
  );

  const setFileToViewExtended: SetFileToViewFunc = (file, props) => {
    setFileToView(file);

    if (props?.marginTop) setViewerTopMargin(props.marginTop);
  };

  return (
    <FileToViewContext.Provider
      value={{
        loadingState,
        setLoadingState,
        fileToView,
        setFileToView: setFileToViewExtended,
        viewerTopMargin,
        setViewerTopMargin,
        primaryPreviewFile,
        setPrimaryPreviewFile,
      }}>
      {children}
    </FileToViewContext.Provider>
  );
};

export const useFileViewerContext = () => useContext(FileToViewContext);
